import { hasPermissionToEditFile } from '@/app/actions';
import { openCodeEditor } from '@/app/grid/actions/openCodeEditor';
import { sheets } from '@/app/grid/controller/Sheets.js';
import type { HtmlCell } from '@/app/gridGL/HTMLGrid/htmlCells/HtmlCell';
import { htmlCellsHandler } from '@/app/gridGL/HTMLGrid/htmlCells/htmlCellsHandler';
import { DOUBLE_CLICK_TIME } from '@/app/gridGL/interaction/pointer/pointerUtils.js';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import * as gridSettings from '@/app/gridGL/pixiApp/gridSettings';
import type { FederatedPointerEvent, Point } from 'pixi.js';

export class PointerHtmlCells {
  private resizing: HtmlCell | undefined; // cell that is being resized
  private hovering: HtmlCell | undefined; // cell whose edge is hovered for resize
  private active: HtmlCell | undefined; // cell that is selected with pointer down
  private clicked: HtmlCell | undefined; // cell that is clicked
  private doubleClickTimeout?: number;

  cursor: string | undefined;

  get isResizing(): boolean {
    return !!this.resizing;
  }

  pointerMove(world: Point, e: FederatedPointerEvent): boolean {
    if (!hasPermissionToEditFile(gridSettings.getPermissions())) return false;

    if (this.resizing) {
      this.resizing.pointerMove(world);
      return true;
    }

    // Don't show resize handles during a move operation
    if (pixiApp.pointer.pointerCellMoving.state === 'move') {
      if (this.hovering) {
        this.hovering.clearHighlightEdges();
        this.hovering = undefined;
      }
      this.cursor = undefined;
      return false;
    }

    this.clicked = undefined;

    const cells = htmlCellsHandler.getCells();
    const selectedTableNames = sheets.sheet.cursor.getSelectedTableNames();

    for (const cell of cells) {
      if (cell.sheet !== sheets.sheet) continue;

      const target = cell.hover(e);
      // pointer hover over chart edge - only show resize cursor if selected
      if (target === 'right' || target === 'bottom' || target === 'corner') {
        const isSelected = selectedTableNames.includes(cell.htmlCell.name);
        if (isSelected) {
          if (this.hovering && this.hovering !== cell) {
            this.hovering.clearHighlightEdges();
          }
          this.hovering = cell;
          this.cursor = target === 'corner' ? 'all-scroll' : target === 'right' ? 'col-resize' : 'row-resize';
          return true;
        }
      }

      // pointer hover over chart
      if (target === 'body') {
        if (this.hovering) {
          this.hovering.clearHighlightEdges();
          this.hovering = undefined;
        }
        this.cursor = undefined;
        return false;
      }
    }
    this.cursor = undefined;
    if (this.hovering) {
      this.hovering.clearHighlightEdges();
      this.hovering = undefined;
    }
    return false;
  }

  pointerDown(e: FederatedPointerEvent): boolean {
    if (!hasPermissionToEditFile(gridSettings.getPermissions())) return false;

    if (e.button !== 0) return false;

    const cells = htmlCellsHandler.getCells();
    const selectedTableNames = sheets.sheet.cursor.getSelectedTableNames();

    for (const cell of cells) {
      if (cell.sheet !== sheets.sheet) continue;
      const target = cell.hover(e);
      const isSelected = selectedTableNames.includes(cell.htmlCell.name);

      // pointer down on chart edge, start resizing (only if already selected)
      if ((target === 'right' || target === 'bottom' || target === 'corner') && isSelected) {
        this.resizing = cell;
        this.resizing.startResizing();
        this.cursor = target === 'corner' ? 'nwse-resize' : target === 'right' ? 'col-resize' : 'row-resize';
        htmlCellsHandler.movetoTop(cell);
        return true;
      }

      // pointer down on chart body or edge (if not selected)
      // select code cell, move chart to top and start double click timer
      if (target === 'body' || target === 'right' || target === 'bottom' || target === 'corner') {
        const cursor = sheets.sheet.cursor;

        // double click
        if (this.clicked === cell) {
          this.clicked = undefined;
          this.clearDoubleClick();
          openCodeEditor();
        }
        // click with meta / ctrl key
        // select cell and add to selection
        else if (e.metaKey || e.ctrlKey) {
          cursor.selectTable(cell.htmlCell.name, undefined, false, true);
        }
        // click without meta / ctrl key
        // select cell and clear selection
        else {
          this.active = cell;
          cursor.selectTable(cell.htmlCell.name, undefined, false, false);
        }
        // move chart to top, useful in case of overlapping charts
        htmlCellsHandler.movetoTop(cell);
        return true;
      }
    }
    return false;
  }

  pointerUp(e: FederatedPointerEvent): boolean {
    if (!hasPermissionToEditFile(gridSettings.getPermissions())) return false;

    const active = this.active;
    this.active = undefined;

    if (this.resizing) {
      this.resizing.completeResizing();
      this.resizing = undefined;
      return true;
    }

    // add double click timer
    const cells = htmlCellsHandler.getCells();
    for (const cell of cells) {
      if (cell.sheet !== sheets.sheet) continue;

      const target = cell.hover(e);
      if (target === 'body' && cell === active) {
        this.setDoubleClick();
        this.clicked = cell;
        return true;
      }
      if (target) return false;
    }
    return false;
  }

  handleEscape(): boolean {
    if (this.resizing) {
      this.resizing.cancelResizing();
      this.resizing = undefined;
    }
    return false;
  }

  private setDoubleClick(): void {
    this.clearDoubleClick();
    this.doubleClickTimeout = window.setTimeout(() => {
      this.doubleClickTimeout = undefined;
      this.clicked = undefined;
    }, DOUBLE_CLICK_TIME);
  }

  private clearDoubleClick(): void {
    window.clearTimeout(this.doubleClickTimeout);
    this.doubleClickTimeout = undefined;
  }

  destroy(): void {
    this.clearDoubleClick();
  }
}
